import axiosInstance from '@/app/api/axiosInstance';
import type {
    PreviouslyOrderedItem,
    SalesOrder,
    SalesOrderCreate,
    SalesOrderItemCreate,
    SalesOrderListResponse,
} from '@/app/types/salesOrder';

const SALES_INVOICE_VOUCHER_TYPE_ID = 1;

/** How many past invoices to mine for the "Usually ordered" list. */
const RECENT_INVOICE_DEPTH = 3;

/**
 * Create a sales order.
 *
 * Always submitted as `draft` so it lands where admins already review orders, and
 * without any amount fields — the server recalculates line and document totals and
 * returns the authoritative `total_amount`.
 */
export const createSalesOrder = async (
    payload: SalesOrderCreate
): Promise<SalesOrder> => {
    const response = await axiosInstance.post<SalesOrder>('/sales-orders/', payload);
    return response.data;
};

/**
 * Sales orders created by this user.
 *
 * GET /sales-orders/ resolves a single effective site per request and 400s when the
 * user has neither an accessible site nor a default site preference. A manager may
 * serve parties across sites, so fan out one request per accessible site and merge.
 * Callers must handle an empty `siteIds` by not calling this at all.
 */
export const listMySalesOrders = async (
    userId: number,
    siteIds: number[],
    limit: number = 50
): Promise<SalesOrder[]> => {
    if (siteIds.length === 0) return [];

    const filters = JSON.stringify([
        { field: 'created_by', op: 'eq', value: userId },
    ]);

    const results = await Promise.all(
        siteIds.map(async (siteId) => {
            try {
                const response = await axiosInstance.get<SalesOrderListResponse>(
                    '/sales-orders/',
                    {
                        params: {
                            // The dependency's parameter is named `requested_site_id`,
                            // not `site_id` — passing `site_id` is silently ignored and
                            // every call falls back to the user's default site.
                            requested_site_id: siteId,
                            limit,
                            sort_by: 'transaction_date',
                            sort_dir: 'desc',
                            filters,
                        },
                    }
                );
                return response.data.items || [];
            } catch {
                // One inaccessible site shouldn't blank the whole list.
                return [];
            }
        })
    );

    // Dedupe by id so a site that resolves to the same effective site can't
    // surface the same order twice.
    const byId = new Map<number, SalesOrder>();
    for (const order of results.flat()) byId.set(order.id, order);

    return Array.from(byId.values())
        .sort((a, b) => {
            const byDate = b.transaction_date.localeCompare(a.transaction_date);
            return byDate !== 0 ? byDate : b.id - a.id;
        })
        .slice(0, limit);
};

/** One order with its lines. The list response omits lines, so detail needs this. */
export const getSalesOrder = async (orderId: number): Promise<SalesOrder> => {
    const response = await axiosInstance.get<SalesOrder>(`/sales-orders/${orderId}`);
    return response.data;
};

/**
 * Replace the lines of an existing order.
 *
 * PATCH swaps the entire line set, so `items` must describe the order's final state —
 * a line left out is a line deleted. Amount fields are omitted for the same reason as
 * on create: the server recalculates line and document totals and returns them.
 *
 * Only orders the office has not acted on yet are editable; the server rejects
 * cancelled, completed and invoiced ones with a 400.
 */
export const updateSalesOrderItems = async (
    orderId: number,
    items: SalesOrderItemCreate[]
): Promise<SalesOrder> => {
    const response = await axiosInstance.patch<SalesOrder>(`/sales-orders/${orderId}`, {
        items,
    });
    return response.data;
};

interface RawVoucherItem {
    item_id: number;
    quantity: string | number;
    item?: { id: number; name: string } | null;
}

/**
 * Items this party has bought recently, ranked for the "Usually ordered" list.
 *
 * Built from the party's last few sales invoices. The voucher LIST response returns
 * `items: []` — only the detail response carries lines — so this is one list call
 * plus one detail call per invoice.
 *
 * Historical rates are ignored on purpose: quantities are a useful hint, but the
 * rate charged is always resolved live from current prices and tiers.
 */
export const getPreviouslyOrderedItems = async (
    partyId: number,
    siteIds: number[]
): Promise<PreviouslyOrderedItem[]> => {
    try {
        const filters: Array<{ field: string; op: string; value: number | number[] }> = [
            { field: 'party_id', op: 'eq', value: partyId },
            { field: 'voucher_type_id', op: 'eq', value: SALES_INVOICE_VOUCHER_TYPE_ID },
        ];
        if (siteIds.length > 0) {
            filters.push({ field: 'site_id', op: 'in', value: siteIds });
        }

        const listResponse = await axiosInstance.get<{
            items: Array<{ id: number; transaction_date: string }>;
        }>('/vouchers/', {
            params: {
                limit: RECENT_INVOICE_DEPTH,
                sort_by: 'transaction_date',
                sort_dir: 'desc',
                filters: JSON.stringify(filters),
            },
        });

        const vouchers = listResponse.data.items || [];
        if (vouchers.length === 0) return [];

        const details = await Promise.all(
            vouchers.map(async (voucher) => {
                try {
                    const detail = await axiosInstance.get<{
                        transaction_date: string;
                        items: RawVoucherItem[];
                    }>(`/vouchers/${voucher.id}`);
                    return detail.data;
                } catch {
                    return null;
                }
            })
        );

        const byItem = new Map<number, PreviouslyOrderedItem>();

        // Newest invoice first, so the first quantity seen for an item is its latest.
        for (const detail of details) {
            if (!detail) continue;
            for (const line of detail.items || []) {
                if (!line.item_id) continue;
                const existing = byItem.get(line.item_id);
                if (existing) {
                    existing.orderCount += 1;
                    continue;
                }
                byItem.set(line.item_id, {
                    item_id: line.item_id,
                    name: line.item?.name ?? '',
                    lastQuantity: Number(line.quantity) || 0,
                    orderCount: 1,
                    lastOrderedOn: detail.transaction_date,
                });
            }
        }

        return Array.from(byItem.values()).sort((a, b) => {
            if (b.orderCount !== a.orderCount) return b.orderCount - a.orderCount;
            return b.lastOrderedOn.localeCompare(a.lastOrderedOn);
        });
    } catch (error) {
        console.error('Failed to load previously ordered items:', error);
        return [];
    }
};
